import React, { memo, useRef, useCallback, useMemo, useState, useEffect } from 'react';
import { history } from "umi";
import PageAccess from '@gp/components/PageAccess';
import MultipleLinesTextBubble from '@gp/components/MultipleLinesTextBubble';
import { Card } from "tea-component";
import DataTime from '@gp/components/DataTime';
import Filter from "./components/Filter";
import LegendSign from "@gp/pages/Pipeline/Overview/components/Schedule/components/LegendSign";
import CalendarPanel from '@gp/pages/Pipeline/Overview/components/CalendarPanel';
import useLegendList from '@gp/pages/Pipeline/Overview/components/Schedule/hooks/useLegendList';
import { useRequest } from "ahooks";
import {
  fetchStudioOverallCalendarTimeData,
  fetchStudioOverallCalendarData,
} from '@gp/pages/StudioDashboard/StudioOverall/services/studioOverall';
import { MENU_ID_MAP, SLASH_G_POP, CONSTANT_TEXTS } from '@gp/constants/const';
import { getCommonCalendarList } from './utils';
import moment from 'moment';
import { FIELDS1, FIELD_NAMES} from '@gp/utils/metricTips';
import './index.scss';

import FullScreenScale from '@home/components/FullScreenScale';
import useScalable from '@gp/pages/Pipeline/Overview/components/Schedule/hooks/useScalable';
import {
  CALENDAR_CARD_SIZES,
  DEFAULT_CALENDAR_CARD_SIZE,
  MIN_CALENDAR_CARD_SIZE,
  MAX_CALENDAR_CARD_SIZE,
} from '@gp/pages/Pipeline/Overview/components/CalendarPanel/constants';

import type { ICalendarPanelInstance } from '@gp/pages/Pipeline/Overview/components/CalendarPanel';

const PARAMS_MAP: {
  [keyName: string]: {
    'granularity': 'monthly' | 'quarter' | 'year';
    'selectDateFormat': string;
  };
} = {
  'month': {
    'granularity': 'monthly',
    'selectDateFormat': 'YYYY.MM',
  },
  'quarter': {
    'granularity': 'quarter',
    'selectDateFormat': 'YYYY.Q',
  },
  'year': {
    'granularity': 'year',
    'selectDateFormat': 'YYYY',
  },
};
const LENGEND_SIGN_OPT = [
  {
    dotColor: '#A6C9FF',
    text: 'Project Milestone',
  },
  {
    dotColor: '#5EC8F1',
    text: (
      <MultipleLinesTextBubble
        fields={FIELDS1}
        names={FIELD_NAMES}
        data={{
          definition: 'Studio post-investment management calendar.',
          source: 'Studio Ops',
          updateCadence: 'Ad hoc, update when events is re-scheduled',
        }}
      >
        <span>Management</span>
      </MultipleLinesTextBubble>
    ),
  },
  {
    dotColor: '#B8BED1',
    text: CONSTANT_TEXTS.COMPLETED,
  },
];

const Calendar: React.FC<IStudioOverallCalendarProps> = () => {

  const [dateType, setDateType] = useState<dateTypes>('quarter');
  const [legendSelected, setLegendSelected] = useState<LegendSelectedTypes>([
    true,
    true,
    true,
  ]);
  // 是否没有Calendar日历的权限
  const [isNoPermission, setIsNoPermission] = useState(false);
  const [dataList, setDataList] = useState<CommonCalendarDataTypes>(null);

  const {
    loading: filterTimeLoading,
    data: selectableTime,
  } = useRequest(fetchStudioOverallCalendarTimeData);
  const {
    loading,
    data,
    run,
  } = useRequest(fetchStudioOverallCalendarData, { manual: true });

  const filterFn = useCallback((t: CommonCalendarCardItemTypes, legendSelected: boolean[]) => {
    const isProjectMilestone = t?.type === 1;
    const isManagement = t?.type === 0;
    const isExpired = !!t?.isExpire;
    if (isProjectMilestone && legendSelected?.[0]) return true;
    if (isManagement && legendSelected?.[1]) return true;
    if (isExpired && legendSelected?.[2]) return true;
    return false;
  }, []);

  const { legendList } = useLegendList({
    legendSelected,
    loading,
    dataList,
    dateType,
    filterFn,
  });

  // 计算时间可选范围
  const dateRange: string = useMemo(() => {
    if (filterTimeLoading) return '';
    const startTime = Number(selectableTime?.ms_start_date || '0') < Number(selectableTime?.mgt_start_date || '0') && selectableTime?.ms_start_date
      ? selectableTime?.ms_start_date
      : selectableTime?.mgt_start_date || selectableTime?.ms_start_date || '';
    const endTime = Number(selectableTime?.ms_end_date || '0') > Number(selectableTime?.mgt_end_date || '0') && selectableTime?.ms_end_date
      ? selectableTime?.ms_end_date
      : selectableTime?.mgt_end_date || selectableTime?.ms_end_date || '';
    if (!startTime || !endTime) return '';

    return `${startTime  }-${  endTime}`;
  }, [filterTimeLoading]);

  // 请求页面数据
  const handleFilterChange = useCallback(
    (
      bo: string[],
      studio: string[],
      category: string[],
      dateType: dateTypes,
      date: string[],
    ) => {
      setDateType(dateType);
      const startDateV = moment(date[0], PARAMS_MAP?.[dateType]?.selectDateFormat);
      const endDateV = moment(date[1], PARAMS_MAP?.[dateType]?.selectDateFormat);
      const startDate = startDateV.isValid() ? startDateV.startOf(dateType).format('YYYY-MM-DD') : '';
      const endDate = endDateV.isValid() ? endDateV.endOf(dateType).format('YYYY-MM-DD') : '';
      run({
        'bo': bo || [],
        'studio': studio || [],
        'category': (Array.isArray(category) && category?.length === 1) ? category?.[0] : '',
        'start_date': startDate,
        'end_date': endDate,
        'granularity': PARAMS_MAP?.[dateType]?.granularity,
      });
    },
    [],
  );

  const handleLegendChange = useCallback((v: boolean[]) => {
    setLegendSelected([...v] as LegendSelectedTypes);
  }, []);

  const handleCardClick = useCallback((
    cardList: CommonCalendarCardItemTypes | null,
    isJump = true,
  ) => {
    // 跳转产品详情页
    let url = '';
    if (cardList?.type === 1) {
      const filter = encodeURIComponent(JSON.stringify({
        projectId: cardList?.projectId || '',
        project: cardList?.projectName || '',
        menuFrom: MENU_ID_MAP['pipeline_all-projects'], // 用于标识左侧菜单栏当前选中高亮
        tab: 'gameDetail',
      }));
      url = `${SLASH_G_POP  }/Pipeline/ProjectList/Detail?filter=${filter}`;
    }
    // 跳转Management页签
    const meetingId = cardList?.meetingId || '';
    if (cardList?.type === 0) {
      const filter = encodeURIComponent(JSON.stringify({
        studioId: cardList?.projectId2 || '',
        studioName: cardList?.studio || '',
        menuFrom: MENU_ID_MAP['studio-dashboard_studio-overall'], // 用于标识左侧菜单栏当前选中高亮
        tab: 'management',
        meetingId, // 链接跳转时使用
      }));
      url = `${SLASH_G_POP  }/Studio/PortfolioOverview/Detail?filter=${filter}`;
    }
    if (isJump) {
      // cardList?.type === 0 && setMeetingId(meetingId);
      history.push(url);
    }

    return url;
  }, []);

  useEffect(() => {
    if (loading) return;
    setIsNoPermission(!!data?.no_auth);
    setDataList(getCommonCalendarList(data?.list));
  }, [loading]);

  const calendarPanel = useRef<ICalendarPanelInstance | null>(null);
  const {
    size: calendarCardSize,
    onSmallerSize: handleCalendarCardSmallerSize,
    onBiggerSize: handleCalendarCardBiggerSize,
    reset: resetCalendarCardSize,
  } = useScalable({
    initialSize: DEFAULT_CALENDAR_CARD_SIZE,
    sizes: CALENDAR_CARD_SIZES,
    onSizeChange: (size) => {
      calendarPanel.current?.onCardSizeChange(size);
    },
  });

  return (
    // 暂时没有权限这个页签直接没有，这里不需要做判断
    <PageAccess isAccess={true}>
      <Card className="dbg-studio-overall-calendar">
        <Card.Body
          title={
            <div style={{ display: 'flex', alignItems: 'center' }}>
              Calendar
              {!!selectableTime?.update_time && <DataTime style={{ marginLeft: 6 }} dateType="Month" date={selectableTime?.update_time} />}
            </div>
          }
        >
          <div className="top-info">
            <Filter
              loading={filterTimeLoading}
              dateRange={dateRange}
              onChange={handleFilterChange}
            />
          </div>
          <FullScreenScale
            title="Calendar"
            smallerBtnDisabled={calendarCardSize === MIN_CALENDAR_CARD_SIZE}
            biggerBtnDisabled={calendarCardSize === MAX_CALENDAR_CARD_SIZE}
            onSmaller={handleCalendarCardSmallerSize}
            onBigger={handleCalendarCardBiggerSize}
            onFullScreenChange={() => {
              resetCalendarCardSize();
            }}
          >
            {({ fullScreenContent }) => {
              return (
                <CalendarPanel
                  ref={calendarPanel}
                  isStudioCalendar
                  isLoading={loading || filterTimeLoading}
                  isNoPermission={isNoPermission}
                  calendarCardSize={calendarCardSize}
                  fullScreenContent={fullScreenContent}
                  fixedBottomScrollbarStyle={{ bottom: -10 }}
                  legendSelected={legendSelected}
                  dateType={dateType}
                  data={legendList || []}
                  onCardClick={handleCardClick}
                  topBarRightElement={
                    <>
                      <LegendSign
                        loading={loading || filterTimeLoading}
                        onChange={handleLegendChange}
                        options={LENGEND_SIGN_OPT}
                      />
                    </>
                  }
                />
              );
            }}
          </FullScreenScale>
        </Card.Body>
      </Card>
    </PageAccess>
  );
};

export default memo(Calendar);
